import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { get } from 'lodash-es'
import { Grid, Row, Col, ControlLabel } from 'react-bootstrap'
import { useFlexFlagIsOn } from '@indeed/flex-feature-flags'

import * as filtersActions from 'syft-acp-core/store/filters/actions'
import { fetchAgencies, clearAgencies } from 'syft-acp-core/actions/agencies'
import { Button } from 'syft-acp-atoms/Button'
import usePrevValue from 'syft-acp-util/hooks/usePrevValue'

import {
  updateAgenciesRanks,
  removeFromAgenciesByRankValue,
  makeAgenciesOptions,
  filterByFlexible,
  reorderAgencies,
  getSectionAgencies,
} from './dataAgenciesDropdownHelpers'
import DataAgenciesDropdownItem from './DataAgenciesDropdownItem'

import type {
  AgencySection,
  DataAgenciesDropdownResult,
  MakeAgenciesMap,
} from './DataAgenciesDropdown.types'

import './DataAgenciesDropdown.css'

export const AGENCY_RANK_DND_FLAG = 'stsmpd_1295_agency_rank_drag_and_drop'

/**
 * The primary and other agency dropdowns render as two separate component instances, so a drag that
 * crosses sections has to share the dragged agency's rank between them. Drag state is inherently
 * transient to the gesture (not React render state), so we keep it in a tiny module-scoped holder
 * rather than threading it through Redux. The HTML5 dataTransfer payload is used as a fallback for
 * environments that expose it.
 */
const dragState: { sourceRank: number | null } = { sourceRank: null }

interface DataAgenciesDropdownImplementationProps {
  type: 'primary' | 'other'
  value?: DataAgenciesDropdownResult[] | null
  disabled?: boolean
  editable: boolean
  onChange: (
    newList: Pick<DataAgenciesDropdownResult, 'agency_id' | 'flexible' | 'rank' | 'work_location_id'>[],
  ) => void
  header: string
  agencies: MakeAgenciesMap
  data?: { id: number; platformID: number | null } | null
  actions: {
    fetchAgencies: (platformID: number | null) => void
    clearAgencies: () => void
  }
}

function getOptions(platformID: number | null, agencies: MakeAgenciesMap): { id: number; label: string }[] {
  if (platformID !== 1) {
    return makeAgenciesOptions({ agencies })
  }

  return []
}

function DataAgenciesDropdown({
  type,
  value: _value = [],
  disabled = false,
  editable,
  onChange,
  header,
  agencies,
  data,
  actions,
}: DataAgenciesDropdownImplementationProps) {
  const isAllowed24HoursDeletion = useFlexFlagIsOn('pte_41607_last_minute_booking_cancels')
  const isDragAndDropEnabled = useFlexFlagIsOn(AGENCY_RANK_DND_FLAG)
  const value = useMemo(() => _value ?? [], [_value])
  const platformID = useMemo(() => data?.platformID ?? null, [data])
  const prevPlatformID = usePrevValue(platformID)
  const [options, setOptions] = useState(() => getOptions(platformID, agencies))

  useEffect(() => {
    setOptions(getOptions(platformID, agencies))
  }, [platformID, agencies])

  useEffect(() => {
    if (prevPlatformID !== platformID) {
      actions.fetchAgencies(platformID)
    }
  }, [actions, platformID, prevPlatformID])

  useEffect(() => {
    return () => actions.clearAgencies()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this should be called only on unmount
  }, [])

  const handleChangeDropdown = useCallback(
    (agencyId: number, rank: number, id: number | null) => {
      if (!data?.id) return

      const updatedResults = updateAgenciesRanks({
        data: { id: data.id },
        results: value ?? [],
        type,
        value: agencyId,
        rank,
        id,
      })

      onChange(
        updatedResults.map(agency => ({
          ...agency,
          // Clear id field when updating list
          id: null,
        })),
      )
    },
    [onChange, data, value, type],
  )

  const handleChangeAllowCancellations = useCallback(
    (agencyId: number | null) => {
      if (!data?.id) return
      const updatedResults = value.map(agency => {
        if (agency.agency_id === agencyId) {
          return {
            ...agency,
            allow_short_notice_booking_cancellations: !agency.allow_short_notice_booking_cancellations,
          }
        }
        return agency
      })
      onChange(updatedResults)
    },
    [onChange, data, value],
  )

  const handleAddNewAgency = useCallback(() => {
    if (!data?.id) return

    const sortResultsByRank = [...value].sort(
      (first, second) => get(first, 'rank', 0) - get(second, 'rank', 0),
    )

    // Split sorted results into primary and other agency lists
    const primaryAgencyResults = sortResultsByRank.filter(filterByFlexible('primary'))
    const otherAgencyResults = sortResultsByRank.filter(filterByFlexible('other'))

    /**
     * When adding to 'primary' list make sure to re-rank other agencies
     */
    if (type === 'primary') {
      const newRank = get(primaryAgencyResults, `[${primaryAgencyResults.length - 1}].rank`, 0) + 1
      const newPrimaryAgenciesData = [
        ...primaryAgencyResults.map(agency => ({
          ...agency,
          // Clear id field when updating list
          id: null,
        })),
        {
          agency_id: null,
          rank: newRank,
          work_location_id: data.id,
          flexible: false,
        },
      ]
      const newOtherAgenciesData = otherAgencyResults.map((agency, index) => ({
        ...agency,
        flexible: true,
        rank: 1 + index + newRank,
        // Clear id field when updating list
        id: null,
      }))
      const allData = [...newPrimaryAgenciesData, ...newOtherAgenciesData]
      /**
       * Make sure to propogate change to the upper state
       * to update other agencies dropdowns
       */
      onChange(allData)
    } else {
      const flexibleResults = [
        ...primaryAgencyResults.map(agency => ({
          ...agency,
          flexible: false,
          // Clear id field when updating list
          id: null,
        })),
        ...otherAgencyResults.map(agency => ({
          ...agency,
          flexible: true,
          // Clear id field when updating list
          id: null,
        })),
      ]
      const rank = get(flexibleResults, `[${flexibleResults.length - 1}].rank`, 0) + 1
      const allData = [
        ...flexibleResults,
        {
          agency_id: null,
          rank,
          work_location_id: data.id,
          flexible: true,
        },
      ]
      /**
       * Make sure to propogate change to the upper state
       * to update other agencies dropdowns
       */
      onChange(allData)
    }
  }, [data, value, type, onChange])

  const handleDeleteClick = useCallback(
    (rank: number) => {
      const updatedResults = removeFromAgenciesByRankValue({
        results: value,
        rank,
      })

      onChange(
        updatedResults.map(agency => ({
          ...agency,
          // Clear id field when updating list
          id: null,
        })),
      )
    },
    [value, onChange],
  )

  // Transient drag UI state. Updated only from event handlers (never during render) so the render
  // phase stays pure. `draggingRank` highlights the row being dragged; `dragTargetRank` highlights
  // the row currently under the pointer.
  const [draggingRank, setDraggingRank] = useState<number | null>(null)
  const [dragTargetRank, setDragTargetRank] = useState<number | null>(null)
  // Highlights the whole section dropzone while a row is dragged over it (e.g. the empty Primary box).
  const [isSectionDragOver, setIsSectionDragOver] = useState(false)

  const moveAgency = useCallback(
    (sourceRank: number, targetSection: AgencySection, beforeRank: number | null) => {
      if (!data?.id) return
      onChange(
        reorderAgencies({
          results: value,
          sourceRank,
          targetSection,
          beforeRank,
          data: { id: data.id },
        }),
      )
    },
    [data, value, onChange],
  )

  const handleDragStart = useCallback((rank: number) => {
    dragState.sourceRank = rank
    setDraggingRank(rank)
  }, [])

  const handleDragEnd = useCallback(() => {
    dragState.sourceRank = null
    setDraggingRank(null)
    setDragTargetRank(null)
    setIsSectionDragOver(false)
  }, [])

  const handleDragOverItem = useCallback((rank: number) => {
    setDragTargetRank(rank)
  }, [])

  // Drop onto a specific row. For a same-section drag we honour the drag direction so the dragged
  // row lands where the pointer is: dropping on a row *below* the source lands the dragged row after
  // that target, dropping on a row *above* lands it before. Cross-section drops always insert before
  // the target row.
  const handleDropItem = useCallback(
    (targetRank: number) => {
      const { sourceRank } = dragState
      dragState.sourceRank = null
      setDraggingRank(null)
      setDragTargetRank(null)
      setIsSectionDragOver(false)
      if (sourceRank == null || sourceRank === targetRank) return

      const sectionAgencies = getSectionAgencies(value, type)
      const sourceIndex = sectionAgencies.findIndex(agency => agency.rank === sourceRank)
      const targetIndex = sectionAgencies.findIndex(agency => agency.rank === targetRank)
      const isSameSectionDownward = sourceIndex !== -1 && targetIndex !== -1 && sourceIndex < targetIndex

      if (isSameSectionDownward) {
        // Insert after the target: anchor on the row following the target (append if it is last).
        const afterTarget = sectionAgencies[targetIndex + 1]
        moveAgency(sourceRank, type, afterTarget ? afterTarget.rank : null)
      } else {
        moveAgency(sourceRank, type, targetRank)
      }
    },
    [value, type, moveAgency],
  )

  // Drop onto the section container (below the last row, or into an empty section): append.
  const handleDropInSection = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      const { sourceRank } = dragState
      dragState.sourceRank = null
      setDraggingRank(null)
      setDragTargetRank(null)
      setIsSectionDragOver(false)
      if (sourceRank == null) return
      moveAgency(sourceRank, type, null)
    },
    [type, moveAgency],
  )

  const handleDragOverSection = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      // Only highlight when a drag is actually in progress (dragState is set during a row drag).
      if (dragState.sourceRank != null && !isSectionDragOver) setIsSectionDragOver(true)
    },
    [isSectionDragOver],
  )

  const handleDragLeaveSection = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    // Ignore leave events fired when moving between child rows inside the same dropzone.
    if (event.currentTarget.contains(event.relatedTarget as Node)) return
    setIsSectionDragOver(false)
  }, [])

  // Keyboard reordering within the section (accessible alternative to dragging).
  const handleMoveUp = useCallback(
    (rank: number) => {
      const sectionAgencies = getSectionAgencies(value, type)
      const currentIndex = sectionAgencies.findIndex(agency => agency.rank === rank)
      if (currentIndex <= 0) return
      // Insert before the previous neighbour.
      moveAgency(rank, type, sectionAgencies[currentIndex - 1].rank)
    },
    [value, type, moveAgency],
  )

  const handleMoveDown = useCallback(
    (rank: number) => {
      const sectionAgencies = getSectionAgencies(value, type)
      const currentIndex = sectionAgencies.findIndex(agency => agency.rank === rank)
      if (currentIndex === -1 || currentIndex >= sectionAgencies.length - 1) return
      // Insert before the row two positions down (or append when the next neighbour is last).
      const afterNext = sectionAgencies[currentIndex + 2]
      moveAgency(rank, type, afterNext ? afterNext.rank : null)
    },
    [value, type, moveAgency],
  )

  // Keyboard-accessible section transfer (the non-drag equivalent of dragging a row between the
  // Primary and Other lists). Moves the row to the end of the opposite section, preserving its
  // metadata (reorderAgencies only flips `flexible`).
  const handleToggleSection = useCallback(
    (rank: number) => {
      const destination: AgencySection = type === 'primary' ? 'other' : 'primary'
      moveAgency(rank, destination, null)
    },
    [type, moveAgency],
  )

  const deletable = editable && !disabled

  const agenciesData = getSectionAgencies(value, type)
  const agencyExist = agenciesData.length > 0
  const canReorder = isDragAndDropEnabled && editable && !disabled

  return (
    <div className="DataAgenciesDropdown">
      {agencyExist && isAllowed24HoursDeletion && (
        <Grid>
          <Row className="dropdown-row">
            <Col md={1}>
              <ControlLabel>Rank</ControlLabel>
            </Col>
            <Col md={deletable ? 5 : 8}>
              <ControlLabel>Agencies</ControlLabel>
            </Col>
            <Col md={2}>
              <ControlLabel>{'Cancel < 24h'}</ControlLabel>
            </Col>
            {deletable && <Col md={2} />}
          </Row>
        </Grid>
      )}
      <div
        className={
          canReorder
            ? `DataAgenciesDropdown__dropzone${
                isSectionDragOver ? ' DataAgenciesDropdown__dropzone--active' : ''
              }`
            : undefined
        }
        onDragOver={canReorder ? handleDragOverSection : undefined}
        onDragLeave={canReorder ? handleDragLeaveSection : undefined}
        onDrop={canReorder ? handleDropInSection : undefined}
      >
        {canReorder && !agencyExist && (
          <div
            className={`DataAgenciesDropdown__empty${
              isSectionDragOver ? ' DataAgenciesDropdown__empty--active' : ''
            }`}
          >
            Drag {type === 'primary' ? 'a primary' : 'an'} agency here or use New{' '}
            {type === 'primary' ? 'primary ' : ''}agency below.
          </div>
        )}
        {agenciesData.map((agency, index) => (
          <DataAgenciesDropdownItem
            agency={agency}
            deletable={deletable}
            disabled={disabled}
            editable={editable}
            header={header}
            key={agency.rank}
            onChange={handleChangeDropdown}
            onChangeAllowCancellations={handleChangeAllowCancellations}
            onDeleteClick={handleDeleteClick}
            options={options}
            values={value}
            draggable={canReorder}
            position={index}
            total={agenciesData.length}
            onDragStart={canReorder ? handleDragStart : undefined}
            onDragEnd={canReorder ? handleDragEnd : undefined}
            onDragOverItem={canReorder ? handleDragOverItem : undefined}
            onDropItem={canReorder ? handleDropItem : undefined}
            onMoveUp={canReorder ? handleMoveUp : undefined}
            onMoveDown={canReorder ? handleMoveDown : undefined}
            onToggleSection={canReorder ? handleToggleSection : undefined}
            section={type}
            isDragging={canReorder && draggingRank === agency.rank}
            isDragTarget={canReorder && dragTargetRank === agency.rank && draggingRank !== agency.rank}
          />
        ))}
      </div>
      <Button kind="success" onClick={handleAddNewAgency}>
        New {type === 'primary' ? 'primary' : ''} agency
      </Button>
    </div>
  )
}

export default connect(
  // @ts-expect-error -- missing types
  (state, { onChange }) => ({
    actions: {
      setFilter: (_: unknown, results: DataAgenciesDropdownResult[]) => onChange(results),
    },
    agencies: state.agencies.entityMap,
    // @ts-expect-error -- missing types
    hasAgenciesData: state.agencies.hasData,
  }),
  // @ts-expect-error -- missing types
  (dispatch, { actions }) => {
    const boundActions = bindActionCreators(
      {
        ...filtersActions,
        fetchAgencies,
        clearAgencies,
      },
      dispatch,
    )
    return {
      actions: {
        ...boundActions,
        ...actions,
      },
    }
  },
)(DataAgenciesDropdown)
